import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "../../services/api";
import { useAuth } from "../../context/AuthContext";
import {
    FaWhatsapp, FaTimes, FaCheckCircle, FaFileInvoice,
    FaShieldAlt, FaEye, FaEyeSlash, FaLock, FaCopy, FaCheck,
    FaArrowRight, FaKey
} from "react-icons/fa";
import { toast } from "react-hot-toast";

const InquiryForm = ({ product, onClose }) => {
    const { user, isAuthenticated, setSession } = useAuth();
    const navigate = useNavigate();

    const [form, setForm] = useState({
        productId: product?._id || "",
        productName: product?.name || "Commercial Gobo Projector",
        modelNumber: product?.modelNumber || "",
        quantity: 1,
        customerName: user?.name || "",
        email: user?.email || "",
        password: "",
        confirmPassword: "",
        phone: user?.phone || "",
        companyName: user?.companyName || "",
        city: user?.city || "",
        preferredContact: "WhatsApp",
        requirements: "",
    });

    const [showPassword, setShowPassword] = useState(false);
    const [showConfirmPassword, setShowConfirmPassword] = useState(false);
    const [showSuccessPassword, setShowSuccessPassword] = useState(false);
    const [copiedAll, setCopiedAll] = useState(false);
    const [copiedField, setCopiedField] = useState(null);
    const [submitting, setSubmitting] = useState(false);
    const [submittedData, setSubmittedData] = useState(null);

    const onChange = (e) => setForm(prev => ({ ...prev, [e.target.name]: e.target.value }));

    const handleCopy = (text, fieldName) => {
        if (!text) return;
        navigator.clipboard.writeText(text);
        setCopiedField(fieldName);
        toast.success(`${fieldName} copied to clipboard!`);
        setTimeout(() => setCopiedField(null), 2000);
    };

    const handleCopyAllCredentials = (creds, refNum) => {
        const text = `LUMIGO COMMERCIAL CLIENT ACCOUNT\nReference: ${refNum}\nLogin Portal: ${window.location.origin}/login\nEmail / Username: ${creds.email}\nPassword: ${creds.password}\nCustomer Support / WhatsApp: +91 8999427831`;
        navigator.clipboard.writeText(text);
        setCopiedAll(true);
        toast.success("All Account Credentials copied to clipboard!");
        setTimeout(() => setCopiedAll(false), 2500);
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (submitting) return; // Prevent double click

        if (!form.customerName || !form.email || !form.phone) {
            toast.error("Please fill in your name, email, and phone number.");
            return;
        }

        if (!isAuthenticated && form.password) {
            if (form.password.length < 6) {
                toast.error("Password must be at least 6 characters long.");
                return;
            }
            if (form.password !== form.confirmPassword) {
                toast.error("Password and Confirm Password do not match.");
                return;
            }
        }

        setSubmitting(true);
        try {
            const { data } = await api.post("/api/enquiries", form);

            if (data.token && data.user && setSession) {
                setSession(data.token, data.user);
            }

            setSubmittedData(data);
            toast.success("Enquiry registered successfully!");
        } catch (err) {
            const msg = err?.response?.data?.message || "Failed to submit enquiry. Please try again.";
            toast.error(msg);
        } finally {
            setSubmitting(false);
        }
    };

    // SUCCESS SCREEN (WITH DEDICATED CREDENTIALS SAVING MODULE & WHATSAPP HANDOFF)
    if (submittedData) {
        const creds = submittedData.credentials || {
            email: form.email,
            password: form.password || "Provided during registration",
        };
        const refNum = submittedData.enquiryRef || submittedData.enquiry?.enquiryRef;

        return (
            <div className="fixed inset-0 bg-black/75 backdrop-blur-md flex items-center justify-center p-3 sm:p-4 z-50 animate-fadeIn">
                <div className="bg-[#FFFFFF] border border-[#E5E7EB] rounded-3xl p-6 sm:p-8 max-w-lg w-full text-center shadow-2xl relative max-h-[92vh] overflow-y-auto">
                    <button
                        onClick={onClose}
                        className="absolute top-5 right-5 w-8 h-8 rounded-full bg-[#F7F5EF] text-[#687482] hover:text-[#263746] flex items-center justify-center transition"
                    >
                        <FaTimes />
                    </button>

                    <div className="w-16 h-16 bg-[#FFF7E0] text-[#E3B629] rounded-2xl flex items-center justify-center mx-auto mb-4 text-3xl border border-[#E3B629]/40 shadow-xs">
                        <FaCheckCircle />
                    </div>

                    <span className="text-[#E3B629] text-[10px] font-black uppercase tracking-[0.25em] block mb-1">
                        Commercial Enquiry Registered
                    </span>
                    <h2 className="text-2xl font-black text-[#263746] tracking-tight mb-2">
                        Request Received
                    </h2>

                    {/* Reference Card with Copy */}
                    <div className="bg-[#F7F5EF] border border-[#E5E7EB] rounded-2xl p-4 my-3 flex items-center justify-between">
                        <div className="text-left">
                            <span className="text-[10px] text-[#687482] uppercase font-bold tracking-wider block">
                                Enquiry Reference Number
                            </span>
                            <span className="text-lg font-mono font-black text-[#263746]">
                                {refNum}
                            </span>
                        </div>
                        <button
                            type="button"
                            onClick={() => handleCopy(refNum, "Reference Number")}
                            className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-white border border-[#E5E7EB] text-xs font-bold text-[#263746] hover:bg-[#E5E7EB] transition cursor-pointer"
                        >
                            {copiedField === "Reference Number" ? <FaCheck className="text-[#168B53]" /> : <FaCopy />}
                            <span>{copiedField === "Reference Number" ? "Copied" : "Copy"}</span>
                        </button>
                    </div>

                    {/* DEDICATED CLIENT CREDENTIALS MODULE */}
                    <div className="bg-amber-50/70 border-2 border-[#E3B629]/50 rounded-2xl p-4 text-left my-4 shadow-xs">
                        <div className="flex items-center gap-2 mb-2">
                            <FaKey className="text-[#E3B629] text-sm" />
                            <h4 className="text-xs font-black uppercase tracking-wider text-[#263746]">
                                Your Commercial Client Dashboard Credentials
                            </h4>
                        </div>
                        <p className="text-[11px] text-[#687482] mb-3 leading-relaxed">
                            Save these credentials to log in to your dashboard anytime to track quotation drawings, dispatch dates, and order status:
                        </p>

                        <div className="space-y-2 text-xs">
                            {/* Email / Username */}
                            <div className="bg-white rounded-xl p-2.5 border border-[#E5E7EB] flex items-center justify-between">
                                <div>
                                    <span className="text-[10px] uppercase font-bold text-[#687482] block">Username / Login Email</span>
                                    <span className="font-mono font-bold text-[#263746]">{creds.email}</span>
                                </div>
                                <button
                                    type="button"
                                    onClick={() => handleCopy(creds.email, "Username")}
                                    className="p-1.5 rounded bg-[#F7F5EF] hover:bg-[#E5E7EB] text-[#263746] transition text-xs"
                                    title="Copy Username"
                                >
                                    {copiedField === "Username" ? <FaCheck className="text-[#168B53]" /> : <FaCopy />}
                                </button>
                            </div>

                            {/* Password */}
                            <div className="bg-white rounded-xl p-2.5 border border-[#E5E7EB] flex items-center justify-between">
                                <div>
                                    <span className="text-[10px] uppercase font-bold text-[#687482] block">Dashboard Password</span>
                                    <span className="font-mono font-bold text-[#263746]">
                                        {showSuccessPassword ? creds.password : "••••••••••••"}
                                    </span>
                                </div>
                                <div className="flex items-center gap-1.5">
                                    <button
                                        type="button"
                                        onClick={() => setShowSuccessPassword(!showSuccessPassword)}
                                        className="p-1.5 rounded bg-[#F7F5EF] hover:bg-[#E5E7EB] text-[#263746] transition text-xs"
                                        title={showSuccessPassword ? "Hide password" : "Show password"}
                                    >
                                        {showSuccessPassword ? <FaEyeSlash /> : <FaEye />}
                                    </button>
                                    <button
                                        type="button"
                                        onClick={() => handleCopy(creds.password, "Password")}
                                        className="p-1.5 rounded bg-[#F7F5EF] hover:bg-[#E5E7EB] text-[#263746] transition text-xs"
                                        title="Copy Password"
                                    >
                                        {copiedField === "Password" ? <FaCheck className="text-[#168B53]" /> : <FaCopy />}
                                    </button>
                                </div>
                            </div>
                        </div>

                        {/* One-Click Copy All */}
                        <button
                            type="button"
                            onClick={() => handleCopyAllCredentials(creds, refNum)}
                            className="mt-3 w-full bg-[#FFFFFF] hover:bg-[#F7F5EF] border border-[#E3B629] text-[#263746] font-bold py-2 rounded-xl text-xs flex items-center justify-center gap-1.5 transition cursor-pointer"
                        >
                            {copiedAll ? <FaCheck className="text-[#168B53]" /> : <FaCopy className="text-[#E3B629]" />}
                            <span>{copiedAll ? "Credentials Saved to Clipboard!" : "Copy All Credentials to Save"}</span>
                        </button>
                    </div>

                    <div className="space-y-2.5 pt-1">
                        {submittedData.whatsappUrl && (
                            <a
                                href={submittedData.whatsappUrl}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="w-full bg-[#168B53] hover:bg-[#127244] text-white font-bold py-3.5 px-6 rounded-xl flex items-center justify-center gap-2.5 transition shadow-sm text-xs uppercase tracking-wider"
                            >
                                <FaWhatsapp className="text-lg" /> Continue on WhatsApp (+91 8999427831)
                            </a>
                        )}

                        <button
                            type="button"
                            onClick={() => {
                                onClose();
                                navigate("/customer");
                            }}
                            className="w-full bg-[#263746] hover:bg-[#1b2732] text-white font-bold py-3.5 px-6 rounded-xl transition text-xs uppercase tracking-wider flex items-center justify-center gap-2"
                        >
                            <FaFileInvoice className="text-[#E3B629]" />
                            <span>Go to My Client Dashboard</span>
                            <FaArrowRight className="text-[10px]" />
                        </button>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center p-4 z-50 animate-fadeIn">
            <div className="bg-[#FFFFFF] border border-[#E5E7EB] rounded-2xl w-full max-w-xl max-h-[92vh] flex flex-col shadow-2xl overflow-hidden">
                {/* Header */}
                <div className="bg-[#F7F5EF] border-b border-[#E5E7EB] px-6 py-4 flex justify-between items-center">
                    <div className="flex items-center gap-3">
                        <img
                            src="/favicon.png"
                            alt="LUMIGO Aperture"
                            className="w-9 h-9 object-contain drop-shadow-xs flex-shrink-0"
                        />
                        <div>
                            <span className="text-[10px] tracking-widest text-[#E3B629] uppercase font-black block">LUMIGO PROJECTION SOLUTIONS</span>
                            <h2 className="text-lg font-extrabold text-[#263746]">Product Enquiry & Quote Request</h2>
                        </div>
                    </div>
                    <button
                        onClick={onClose}
                        className="text-[#687482] hover:text-[#263746] transition p-1 text-lg"
                    >
                        <FaTimes />
                    </button>
                </div>

                {/* Body Form */}
                <div className="p-6 overflow-y-auto space-y-5 custom-scrollbar">
                    {/* Selected Product Card */}
                    {product && (
                        <div className="flex items-center gap-4 p-3.5 rounded-xl bg-[#F7F5EF] border border-[#E5E7EB]">
                            {product.images && product.images[0] && (
                                <img
                                    src={product.images[0]}
                                    alt={product.name}
                                    className="w-14 h-14 object-contain rounded-lg bg-[#FFFFFF] border border-[#E5E7EB] p-1"
                                />
                            )}
                            <div className="flex-1 min-w-0">
                                <span className="text-[10px] uppercase font-bold text-[#E3B629] tracking-wider block">
                                    Model: {product.modelNumber || "LUMIGO"}
                                </span>
                                <h4 className="text-sm font-bold text-[#263746] truncate">{product.name}</h4>
                                <div className="text-xs text-[#687482] mt-0.5">
                                    Indicative Hardware: <span className="text-[#263746] font-bold">₹{product.price ? product.price.toLocaleString() : "On Request"}</span>
                                </div>
                            </div>
                        </div>
                    )}

                    <form onSubmit={handleSubmit} className="space-y-4 text-xs">
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                            <div>
                                <label className="block text-[#263746] font-bold mb-1.5 uppercase tracking-wider text-[11px]">
                                    Quantity <span className="text-[#E3B629]">*</span>
                                </label>
                                <input
                                    type="number"
                                    min="1"
                                    name="quantity"
                                    value={form.quantity}
                                    onChange={onChange}
                                    required
                                    className="w-full bg-[#FFFFFF] border border-[#E5E7EB] rounded-lg px-3.5 py-2.5 text-[#263746] focus:border-[#E3B629] outline-none transition"
                                />
                            </div>

                            <div>
                                <label className="block text-[#263746] font-bold mb-1.5 uppercase tracking-wider text-[11px]">
                                    Preferred Contact <span className="text-[#E3B629]">*</span>
                                </label>
                                <select
                                    name="preferredContact"
                                    value={form.preferredContact}
                                    onChange={onChange}
                                    className="w-full bg-[#FFFFFF] border border-[#E5E7EB] rounded-lg px-3.5 py-2.5 text-[#263746] focus:border-[#E3B629] outline-none transition"
                                >
                                    <option value="WhatsApp">WhatsApp (+91 8999427831)</option>
                                    <option value="Phone">Phone Call</option>
                                    <option value="Email">Email</option>
                                </select>
                            </div>
                        </div>

                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                            <div>
                                <label className="block text-[#263746] font-bold mb-1.5 uppercase tracking-wider text-[11px]">
                                    Your Full Name <span className="text-red-500">*</span>
                                </label>
                                <input
                                    type="text"
                                    name="customerName"
                                    value={form.customerName}
                                    onChange={onChange}
                                    required
                                    placeholder="e.g. Rahul Sharma"
                                    className="w-full bg-[#FFFFFF] border border-[#E5E7EB] rounded-lg px-3.5 py-2.5 text-[#263746] focus:border-[#E3B629] outline-none transition"
                                />
                            </div>

                            <div>
                                <label className="block text-[#263746] font-bold mb-1.5 uppercase tracking-wider text-[11px]">
                                    Phone / WhatsApp <span className="text-red-500">*</span>
                                </label>
                                <input
                                    type="tel"
                                    name="phone"
                                    value={form.phone}
                                    onChange={onChange}
                                    required
                                    placeholder="e.g. +91 9876543210"
                                    className="w-full bg-[#FFFFFF] border border-[#E5E7EB] rounded-lg px-3.5 py-2.5 text-[#263746] focus:border-[#E3B629] outline-none transition"
                                />
                            </div>
                        </div>

                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                            <div>
                                <label className="block text-[#263746] font-bold mb-1.5 uppercase tracking-wider text-[11px]">
                                    Email Address (Your Client Login ID) <span className="text-red-500">*</span>
                                </label>
                                <input
                                    type="email"
                                    name="email"
                                    value={form.email}
                                    onChange={onChange}
                                    required
                                    placeholder="name@company.com"
                                    className="w-full bg-[#FFFFFF] border border-[#E5E7EB] rounded-lg px-3.5 py-2.5 text-[#263746] focus:border-[#E3B629] outline-none transition"
                                />
                            </div>

                            <div>
                                <label className="block text-[#263746] font-bold mb-1.5 uppercase tracking-wider text-[11px]">
                                    Company / Business Name
                                </label>
                                <input
                                    type="text"
                                    name="companyName"
                                    value={form.companyName}
                                    onChange={onChange}
                                    placeholder="e.g. Hyatt Hotels / Individual"
                                    className="w-full bg-[#FFFFFF] border border-[#E5E7EB] rounded-lg px-3.5 py-2.5 text-[#263746] focus:border-[#E3B629] outline-none transition"
                                />
                            </div>
                        </div>

                        {/* Client Dashboard Access Password Setup */}
                        {!isAuthenticated ? (
                            <div className="bg-[#FFFBEB] border border-[#F59E0B]/40 rounded-xl p-3.5 space-y-3">
                                <div className="flex items-center justify-between">
                                    <span className="text-[11px] font-bold text-[#92400E] uppercase tracking-wider flex items-center gap-1.5">
                                        <FaKey className="text-[#E3B629]" />
                                        Client Dashboard Login Setup
                                    </span>
                                    <span className="text-[10px] text-[#687482]">Optional (auto-created if blank)</span>
                                </div>
                                <p className="text-[11px] text-[#687482] leading-relaxed">
                                    Set your dashboard password so you can sign in anytime to monitor quotation progress, technical drawings, and order dispatch:
                                </p>
                                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                                    <div>
                                        <label className="block text-[#263746] font-bold mb-1 uppercase tracking-wider text-[10px]">
                                            Set Password
                                        </label>
                                        <div className="relative">
                                            <input
                                                type={showPassword ? "text" : "password"}
                                                name="password"
                                                value={form.password}
                                                onChange={onChange}
                                                placeholder="Choose password (min 6 chars)"
                                                className="w-full bg-[#FFFFFF] border border-[#E5E7EB] rounded-lg pl-8 pr-8 py-2 text-xs text-[#263746] focus:border-[#E3B629] outline-none transition"
                                            />
                                            <FaLock className="absolute left-2.5 top-1/2 -translate-y-1/2 text-[#687482] text-[11px]" />
                                            <button
                                                type="button"
                                                onClick={() => setShowPassword(!showPassword)}
                                                className="absolute right-2.5 top-1/2 -translate-y-1/2 text-[#687482] hover:text-[#263746] text-xs p-1"
                                                title={showPassword ? "Hide password" : "Show password"}
                                            >
                                                {showPassword ? <FaEyeSlash /> : <FaEye />}
                                            </button>
                                        </div>
                                    </div>

                                    <div>
                                        <label className="block text-[#263746] font-bold mb-1 uppercase tracking-wider text-[10px]">
                                            Confirm Password
                                        </label>
                                        <div className="relative">
                                            <input
                                                type={showConfirmPassword ? "text" : "password"}
                                                name="confirmPassword"
                                                value={form.confirmPassword}
                                                onChange={onChange}
                                                placeholder="Re-type password"
                                                className="w-full bg-[#FFFFFF] border border-[#E5E7EB] rounded-lg pl-8 pr-8 py-2 text-xs text-[#263746] focus:border-[#E3B629] outline-none transition"
                                            />
                                            <FaLock className="absolute left-2.5 top-1/2 -translate-y-1/2 text-[#687482] text-[11px]" />
                                            <button
                                                type="button"
                                                onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                                                className="absolute right-2.5 top-1/2 -translate-y-1/2 text-[#687482] hover:text-[#263746] text-xs p-1"
                                                title={showConfirmPassword ? "Hide password" : "Show password"}
                                            >
                                                {showConfirmPassword ? <FaEyeSlash /> : <FaEye />}
                                            </button>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        ) : (
                            <div className="bg-[#ECFDF5] border border-[#10B981]/30 rounded-xl p-3 flex items-center gap-2.5 text-xs text-[#065F46]">
                                <FaCheckCircle className="text-[#10B981] flex-shrink-0" />
                                <span>Logged in as <strong>{user?.email}</strong>. This quotation enquiry will be automatically linked to your commercial client dashboard.</span>
                            </div>
                        )}

                        <div>
                            <label className="block text-[#263746] font-bold mb-1.5 uppercase tracking-wider text-[11px]">
                                City / Project Location
                            </label>
                            <input
                                type="text"
                                name="city"
                                value={form.city}
                                onChange={onChange}
                                placeholder="e.g. Pune, Mumbai, Bangalore"
                                className="w-full bg-[#FFFFFF] border border-[#E5E7EB] rounded-lg px-3.5 py-2.5 text-[#263746] focus:border-[#E3B629] outline-none transition"
                            />
                        </div>

                        <div>
                            <label className="block text-[#263746] font-bold mb-1.5 uppercase tracking-wider text-[11px]">
                                Project Requirements / Custom Gobo Details
                            </label>
                            <textarea
                                name="requirements"
                                value={form.requirements}
                                onChange={onChange}
                                rows="3"
                                placeholder="Mention approximate projection distance (e.g. 10 metres), outdoor/indoor surface, logo colors, or single vs 4-logo rotation preference..."
                                className="w-full bg-[#FFFFFF] border border-[#E5E7EB] rounded-lg px-3.5 py-2.5 text-[#263746] focus:border-[#E3B629] outline-none transition resize-none"
                            />
                        </div>

                        <div className="pt-2">
                            <button
                                type="submit"
                                disabled={submitting}
                                className="w-full bg-[#E3B629] hover:bg-[#cca021] text-[#263746] font-extrabold py-3.5 px-6 rounded-xl transition duration-200 flex items-center justify-center gap-2 text-xs uppercase tracking-widest shadow-sm disabled:opacity-50 cursor-pointer"
                            >
                                {submitting ? (
                                    <>
                                        <span className="w-4 h-4 border-2 border-[#263746] border-t-transparent rounded-full animate-spin"></span>
                                        <span>Registering Enquiry...</span>
                                    </>
                                ) : (
                                    <>
                                        <FaFileInvoice />
                                        <span>Submit Enquiry & Request Quote</span>
                                    </>
                                )}
                            </button>
                            <p className="text-[11px] text-[#687482] mt-2.5 text-center flex items-center justify-center gap-1.5">
                                <FaShieldAlt className="text-[#E3B629]" /> No online payment required. Official quotation & technical sizing provided directly.
                            </p>
                        </div>
                    </form>
                </div>
            </div>
        </div>
    );
};

export default InquiryForm;
